import { Checkbox } from "@/components/ui/checkbox";
import type {
  CarrierServiceCoverage,
  CarrierServiceCoverageInput,
  CarrierTransportServiceRow,
} from "@/api/carrier";
import { useCarrierCostRates, useCarrierDispatchCenters } from "@/api/carrier";
import { ServiceOfferingGuidance } from "@/features/service-offering-guidance";

export interface ServiceCoverageDraft {
  active: boolean;
}

export function serviceCoverageDrafts(
  coverages: CarrierServiceCoverage[]
): Record<string, ServiceCoverageDraft> {
  return Object.fromEntries(
    coverages.map((coverage) => [
      coverage.carrierServiceId,
      {
        active: coverage.active,
      },
    ])
  );
}

export function serviceCoverageInputs(
  services: CarrierTransportServiceRow[],
  values: Record<string, ServiceCoverageDraft>
): CarrierServiceCoverageInput[] {
  return services.map((service) => {
    const coverage = values[service.id] ?? {
      active: false,
    };
    return {
      carrierServiceId: service.id,
      active: coverage.active,
    };
  });
}

export function validateServiceCoverages(
  services: CarrierTransportServiceRow[],
  values: Record<string, ServiceCoverageDraft>
): string | null {
  for (const service of services) {
    const error = validateServiceCoverage(
      service,
      values[service.id] ?? { active: false }
    );
    if (error) return error;
  }
  return null;
}

export function ServiceAreaCheckbox({
  id,
  label,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label htmlFor={id} className="flex min-h-11 items-center gap-3 text-sm">
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        className="h-5 w-5"
        onCheckedChange={(value) => onChange(value === true)}
      />
      {label}
    </label>
  );
}

export function parseOptionalCapacity(
  input: string
):
  | { value: number | null; error?: undefined }
  | { value?: undefined; error: string } {
  const trimmed = input.trim();
  const value = trimmed === "" ? null : Number(trimmed);
  if (value !== null && (!Number.isInteger(value) || value < 0)) {
    return {
      error: "Maximum daily orders must be a whole non-negative number.",
    };
  }
  return { value };
}

export function validateServiceCoverage(
  service: Pick<CarrierTransportServiceRow, "name" | "estimatedDays">,
  coverage: ServiceCoverageDraft
): string | null {
  if (!coverage.active) return null;
  if (service.estimatedDays == null) {
    return `${service.name} needs estimated delivery days before it can be enabled.`;
  }
  return null;
}

export function ServiceCoverageFields({
  services,
  values,
  savedValues,
  areaId,
  disabled,
  onChange,
}: {
  services: CarrierTransportServiceRow[];
  values: Record<string, ServiceCoverageDraft>;
  savedValues: Record<string, ServiceCoverageDraft>;
  areaId?: string;
  disabled: boolean;
  onChange: (serviceId: string, value: ServiceCoverageDraft) => void;
}) {
  const rates = useCarrierCostRates();
  const dispatchCenters = useCarrierDispatchCenters();
  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-medium">Carrier services</legend>
      <p className="text-xs text-muted-foreground">
        Feasibility follows pickup or handoff timing, processing, and delivery
        schedules.
      </p>
      {services.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Create an active shipping service before configuring area coverage.
        </p>
      ) : (
        services.map((service) => {
          const value = values[service.id] ?? {
            active: false,
          };
          const inputId = `service-coverage-${service.id}`;
          return (
            <div key={service.id} className="rounded-md border p-3">
              <ServiceAreaCheckbox
                id={inputId}
                label={service.name}
                checked={value.active}
                disabled={disabled || service.estimatedDays == null}
                onChange={(active) =>
                  onChange(service.id, { ...value, active })
                }
              />
              <p className="ml-8 text-xs text-muted-foreground">
                {service.estimatedDays == null
                  ? "Estimated delivery days must be configured first."
                  : service.estimatedDays === 0
                    ? "Delivery on the induction day when a run is available"
                    : `${service.estimatedDays} business day${service.estimatedDays === 1 ? "" : "s"}`}
              </p>
              {!value.active && (
                <p className="ml-8 text-xs text-muted-foreground">
                  {savedValues[service.id]?.active
                    ? "Save this change to remove this service's area offering."
                    : "Not offered in this area. Turn on the checkbox above and save to offer it."}
                </p>
              )}
              {value.active && !savedValues[service.id]?.active && (
                <p className="ml-8 text-xs text-muted-foreground">
                  Save this new offering before it can appear in quotes.
                </p>
              )}
              {value.active && (
                <div className="mt-3 ml-8">
                  <ServiceOfferingGuidance
                    service={service}
                    areaId={areaId}
                    labelBands={rates.data?.labelBands}
                    dispatchCenters={dispatchCenters.data}
                  />
                </div>
              )}
            </div>
          );
        })
      )}
    </fieldset>
  );
}
